import { DecimalPipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  Signal,
  signal,
  viewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  applyEach,
  form,
  FormField,
  min,
  required,
} from '@angular/forms/signals';
import { Bundle, Country } from '@core/core.types';
import { GetCountriesAction } from '@core/store/countries/countries.actions';
import { CountriesState } from '@core/store/countries/countries.state';
import { ProductsListState } from '@core/store/products/products.state';
import { momentToNgbDate } from '@core/utils/core.util';
import {
  NgbActiveModal,
  NgbDatepickerModule,
  NgbTypeaheadModule,
  NgbTypeaheadSelectItemEvent,
} from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Store } from '@ngxs/store';
import { ConfirmService } from '@shared/components/confirm/confirm-dialog.component';
import { SelectOptionComponent } from '@shared/components/select/select-option/select-option.component';
import { SelectComponent } from '@shared/components/select/select.component';
import { ToastService } from '@shared/components/toast-container/toast.service';
import { ConfirmCloseReason } from '@shared/shared.types';
import moment from 'moment';
import {
  debounceTime,
  distinctUntilChanged,
  map,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { PricingCountryService } from '../../../services/pricing-country.service';
import { AddPricingCountryBundlePriceAction } from '../../../store/pricing-country.actions';
import { PricingCountryState } from '../../../store/pricing-country.state';
import { CountryPriceType, PricingCountry } from '../../../types/pricing.types';

interface CountryBulkPriceRow {
  bundleId: string;
  name: string;
  currentPrice: number | null;
}

interface CountryBulkPriceModel {
  validFrom: moment.Moment | null;
  prices: { bundleId: string; price: number | null }[];
}

@Component({
  selector: 'app-country-bulk-price-dialog',
  imports: [
    FormField,
    FormsModule,
    TranslateModule,
    NgbDatepickerModule,
    NgbTypeaheadModule,
    DecimalPipe,
    SelectComponent,
    SelectOptionComponent,
  ],
  templateUrl: './country-bulk-price-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrl: './country-bulk-price-dialog.component.scss',
})
export class CountryBulkPriceDialogComponent {
  public countryId = signal<string>(null);

  protected modalRef = inject(NgbActiveModal);
  private store = inject(Store);
  private pricingCountryService = inject(PricingCountryService);
  private toastService = inject(ToastService);
  private translateService = inject(TranslateService);
  private confirmService = inject(ConfirmService);

  protected pricingCountry: Signal<PricingCountry> = computed(() =>
    this.store.selectSignal(PricingCountryState.getPricingCountryById)()(
      this.countryId()
    )
  );
  /** Which price type is being edited; switching it rebuilds the rows. */
  protected priceType = signal<CountryPriceType>(CountryPriceType.EUP);
  protected readonly priceTypes = Object.values(CountryPriceType);
  private priceTypeSelect = viewChild<SelectComponent>('priceTypeSelect');

  private bundlesList: Signal<Bundle[]> = computed(() =>
    this.store.selectSignal(ProductsListState.getBundlesList)()
  );
  private countries: Signal<Country[]> = this.store.selectSignal(
    CountriesState.getCountries
  );

  /** Bundles assigned to the country, plus any already priced for the type. */
  protected rows = computed<CountryBulkPriceRow[]>(() => {
    const countryName = this.pricingCountry()?.name?.toLowerCase();
    const configuredCountry = this.countries()?.find(
      (country) => country.name?.toLowerCase() === countryName
    );
    const priced = (this.pricingCountry()?.pricelist ?? []).filter(
      (bundle) => bundle.pivot?.type === this.priceType()
    );
    const priceByBundleId = new Map(
      priced.map((bundle) => [bundle.id, bundle.pivot?.price ?? null])
    );
    const nameByBundleId = new Map<string, string>(
      (this.bundlesList() ?? []).map((bundle) => [
        bundle.id,
        bundle.internalName ?? bundle.name,
      ])
    );
    priced.forEach((bundle) =>
      nameByBundleId.set(bundle.id, bundle.internalName ?? bundle.name)
    );

    const bundleIds = new Set<string>([
      ...(configuredCountry?.bundleIds ?? []),
      ...priced.map((bundle) => bundle.id),
    ]);

    return [...bundleIds]
      .map((bundleId) => ({
        bundleId,
        name: nameByBundleId.get(bundleId) ?? bundleId,
        currentPrice: priceByBundleId.get(bundleId) ?? null,
      }))
      .sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { sensitivity: 'base' })
      );
  });

  protected priceModel = signal<CountryBulkPriceModel>({
    validFrom: null,
    prices: [],
  });
  protected priceForm = form(this.priceModel, (path) => {
    required(path.validFrom);
    applyEach(path.prices, (pricePath) => {
      min(pricePath.price, 1);
    });
  });

  /** Country picked in the "copy prices from country" typeahead. */
  protected copyFromCountry: PricingCountry | null = null;
  protected copyingFromCountry = signal<boolean>(false);

  protected searchCountries = (text$: Observable<string>) =>
    text$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term) =>
        term?.trim().length
          ? this.pricingCountryService
              .getPricingCountriesList({ name: term.trim() })
              .pipe(
                map((list) =>
                  list.data.filter((country) => country.id !== this.countryId())
                )
              )
          : of([])
      )
    );

  protected countryFormatter = (country: PricingCountry) => country?.name ?? '';

  protected minDate = moment().add(1, 'day');
  protected maxDate = moment().add(100, 'year');
  protected startDate = moment().add(1, 'month');
  protected readonly momentToNgbDate = momentToNgbDate;

  constructor() {
    this.store.dispatch(new GetCountriesAction());

    effect(() => {
      this.resetPrices(this.rows());
    });
  }

  /** Discards typed/copied values: every input shows the current price. */
  private resetPrices(rows: CountryBulkPriceRow[]) {
    this.priceModel.update((model) => ({
      ...model,
      prices: rows.map((row) => ({
        bundleId: row.bundleId,
        price: row.currentPrice,
      })),
    }));
    this.copyFromCountry = null;
  }

  private changedRows = computed(() =>
    this.rows()
      .map((row, index) => ({
        row,
        price: this.priceModel().prices[index]?.price ?? null,
      }))
      .filter(
        ({ row, price }) =>
          price !== null &&
          price !== undefined &&
          Number(price) !== Number(row.currentPrice)
      )
  );

  protected hasChanges = computed(() => this.changedRows().length > 0);

  protected submitDisabled = computed(
    () =>
      !this.rows().length || this.priceForm().invalid() || !this.hasChanges()
  );

  protected onPriceTypeChange(type: CountryPriceType) {
    if (!type || type === this.priceType()) {
      return;
    }

    if (!this.hasChanges()) {
      this.switchPriceType(type);
      return;
    }

    this.confirmService
      .confirm({
        title: this.translateService.instant(
          'pricing.country.details.bulkEdit.discardChanges.title'
        ),
        message: this.translateService.instant(
          'pricing.country.details.bulkEdit.discardChanges.message',
          { type: this.priceType() }
        ),
      })
      .subscribe((result) => {
        if (result.closeReason === ConfirmCloseReason.Yes) {
          this.switchPriceType(type);
        } else {
          // put the select back to the type still being edited
          this.priceTypeSelect()?.writeValue(this.priceType());
        }
      });
  }

  private switchPriceType(type: CountryPriceType) {
    this.priceType.set(type);
    // rebuild right away with the other type's current prices (typed or
    // copied values are dropped), independent of effect scheduling
    this.resetPrices(this.rows());
  }

  protected onCopyCountrySelected(
    event: NgbTypeaheadSelectItemEvent<PricingCountry>
  ) {
    const country = event.item;

    if (!country) {
      return;
    }

    this.copyingFromCountry.set(true);

    this.pricingCountryService
      .getPricingCountryBundles(country.id, this.priceType())
      .subscribe({
        next: (bundles) => {
          this.copyingFromCountry.set(false);

          const priceByBundleId = new Map(
            bundles
              .filter((bundle) => !!bundle?.pivot?.price)
              .map((bundle) => [bundle.id, bundle.pivot.price])
          );
          let copied = 0;

          this.priceModel.update((model) => ({
            ...model,
            prices: model.prices.map((entry) => {
              const price = priceByBundleId.get(entry.bundleId);

              if (price === undefined) {
                return entry;
              }

              copied++;
              return { ...entry, price };
            }),
          }));

          if (!copied) {
            this.toastService.info(
              this.translateService.instant(
                'pricing.country.details.bulkEdit.copyFromCountryNoPrices',
                { country: country.name, type: this.priceType() }
              )
            );
          }
        },
        error: (error) => {
          this.copyingFromCountry.set(false);
          this.toastService.danger(error.error?.message, error.error?.error);
        },
      });
  }

  protected save() {
    if (this.submitDisabled()) {
      return;
    }

    const validFrom = this.priceModel().validFrom?.toDate() ?? null;
    const actions = this.changedRows().map(
      ({ row, price }) =>
        new AddPricingCountryBundlePriceAction(
          this.countryId(),
          row.bundleId,
          Number(price),
          this.priceType(),
          validFrom
        )
    );

    this.store.dispatch(actions).subscribe(() => this.modalRef.close());
  }
}
